import type { LIB_partStoreData$result, PartScreenStageOne$result } from '$houdini'
import type { WritableDeep } from 'type-fest'
import type { OptionValueMap, SettingValues } from 'utility/load-part'
import type { EnterprisePermissionValue } from 'utility/permission'
import { getOptionValueMap, loadPart, computeTypeFields } from 'utility/load-part'
import pProps from 'p-props'
import { graphql } from '$houdini'
import loadModelsForManufacturer, { type PartModel } from 'utility/load-models-for-manufacturer'
import { klona } from 'klona'
import ObjectMap from 'classes/ObjectMap'
import { getObject } from '@isoftdata/utility-storage'
import loadVehicleModelsForMake from 'utility/load-models-for-make'
import makeCallbackWritable from 'stores/callback-writable'

/**
 * Loads everything the Part screen (PartEditor.svelte) needs for a single part.
 * Used by the app.part state's resolve, and by screens that embed the PartEditor (e.g. vehicle teardown).
 */
export async function loadPartScreenData({
	sku,
	storeId,
	useCachedPart = false,
}: {
	sku: number | null
	storeId: number
	useCachedPart?: boolean
}) {
	const { data: settingValuesData } = await settingValuesQuery.fetch({ policy: 'CacheOrNetwork' })

	if (!settingValuesData) {
		throw new Error('No data returned from settingValuesQuery')
	}

	const { settingValues, userSettings } = settingValuesData

	const { part, attachments } = await loadPart({
		sku,
		useCachedPart,
		settingValues,
		userSettings,
		storeId,
	})

	const { data: stageOneData }: { data: WritableDeep<PartScreenStageOne$result> | null } = await stageOneQuery.fetch({
		policy: 'CacheOrNetwork',
	})

	if (!stageOneData) {
		throw new Error('No data returned from stageOneQuery')
	}

	const stageTwoLoads: {
		partStoreData: ReturnType<typeof partStoreDataQuery.fetch>
		trueModels?: Promise<Array<PartModel>>
		assyModels?: Promise<Array<PartModel>>
		vehicleModels?: Promise<Array<string>>
	} = {
		// locations at the store the part is at. Used when adding a new location to the part
		partStoreData: partStoreDataQuery.fetch({
			variables: {
				storeId: part.storeId,
			},
			policy: 'CacheOrNetwork',
		}),
	}

	if (part.manufacturer && part.inventoryTypeId) {
		stageTwoLoads.trueModels = loadModelsForManufacturer(part.manufacturer.id, part.inventoryTypeId)
	}

	if (part.parentManufacturer && part.inventoryType?.setId) {
		stageTwoLoads.assyModels = loadModelsForManufacturer(part.parentManufacturer.id, part.inventoryType.setId)
	}

	if (part.vehicleMake) {
		stageTwoLoads.vehicleModels = loadVehicleModelsForMake(part.vehicleMake)
	}

	const { partStoreData, trueModels, assyModels, vehicleModels } = (await pProps(stageTwoLoads)) as {
		partStoreData: { data: LIB_partStoreData$result }
		trueModels?: Array<PartModel>
		assyModels?: Array<PartModel>
		vehicleModels?: Array<string>
	}

	// It's technically possible to have a cached part and not a cached optionValueMap
	const cachedOptionValueMap = getObject<{
		keyParts: Array<'optionId' | 'serialId' | 'serialUuid'>
		entries: ReturnType<OptionValueMap['entries']>
	}>(localStorage, 'cachedOptionValues')
	const optionValueMap =
		useCachedPart && cachedOptionValueMap
			? new ObjectMap(cachedOptionValueMap.keyParts, cachedOptionValueMap.entries)
			: getOptionValueMap(part)

	part.glCategory = stageOneData.glCategories.find(cat => cat.id === part.glCategory?.id) ?? null
	part.saleClass = stageOneData.saleClasses.find(saleClass => saleClass.code === part.saleClass?.code) ?? part.saleClass

	part.userStatus ||= null
	part.coreClass ||= null

	return {
		clearScreenOnSave: makeCallbackWritable(settingValues.inventory.clearScreenOnSave, async clearScreenOnSave => {
			try {
				await setUserSetting.mutate({ input: { parts: { clearScreenOnSave } } })
			} catch (err) {
				console.error('Error setting user setting', err)
			}
		}),
		settingValues,
		userSettings,
		partStoreLocations: partStoreData.data?.locations ?? [],
		clearLocationsOnCopy: partStoreData.data?.store?.settingValues.parts.clearLocationsOnCopy ?? true,
		origPart: klona(part),
		part,
		attachments,
		optionValueMap,
		loadCachedPart: useCachedPart,
		sellPriceClasses: stageOneData.sellPriceClasses.sort((a, b) => {
			const aDisplayName = a.parent?.name ? `${a.parent.name} - ${a.name}` : a.name
			const bDisplayName = b.parent?.name ? `${b.parent.name} - ${b.name}` : b.name
			return aDisplayName.localeCompare(bDisplayName)
		}),
		trueModels: trueModels ?? [],
		assyModels: assyModels ?? [],
		vehicleModels: vehicleModels ?? [],
		storeId,
		typeFields: computeTypeFields(part, settingValues),
		glCategories: stageOneData.glCategories,
		inventoryConditions: stageOneData.inventoryConditions.filter(Boolean),
		inventoryTypeList: stageOneData.inventoryTypeList.map(type => ({
			...type,
			manufacturerList: type?.partManufacturers.sort((a, b) => a.name.localeCompare(b.name)) ?? [],
		})),
		saleClasses: stageOneData.saleClasses,
		userPartStatusList: stageOneData.userPartStatusList,
		vehicleMakes: stageOneData.vehicleMakes,
		userCanEdit: curryUserCanEdit(
			settingValues.inventory.fields,
			new Set(stageOneData.session.userAccount.authorizedStores.map(store => store.id)),
			part.storeId,
			part.skuType.inventories.map(inventory => inventory.store.id),
			stageOneData.session.permissionLevels,
		),
		// maybe we could filter this better on the server, idk
		vendorList:
			stageOneData.vendors.items.filter(vendor => vendor?.code ?? vendor?.companyName ?? vendor?.contactName) ?? [],
	}
}

export type PartScreenData = Awaited<ReturnType<typeof loadPartScreenData>>

graphql(`
	fragment FieldFlags on InventoryField {
		editable
		global
		required: requiredToSave
		visible
		logChanges
	}
`)

graphql(`
	fragment InventorySettings on InventorySettingValues {
		clearScreenOnSave
		defaultDaysToReturn
		defaultDaysToReturnCore
		defaultDaysToReturnCoreToVendor
		defaultDaysToReturnToVendor
		defaultLocationName
		defaultNonReplenishablePartsArePublic
		defaultQuantityForMisc
		defaultQuantityForReplenishable
		defaultQuantityForStandard
		defaultReplenishablePartsArePublic
		defaultReturnable
		defaultGlCategoryId
		defaultNonReplenishablePartsArePublic
		defaultReturnableToVendor
		varianceLocationName # todo get for part store
		fields {
			averageCost {
				...FieldFlags
			}
			averageCoreCost {
				...FieldFlags
			}
			bodyStyle {
				...FieldFlags
			}
			buyPackage {
				...FieldFlags
			}
			category {
				...FieldFlags
			}
			condition {
				...FieldFlags
			}
			coreClass {
				...FieldFlags
			}
			coreCost {
				...FieldFlags
			}
			coreRequired {
				...FieldFlags
			}
			coreRequiredToVendor {
				...FieldFlags
			}
			cost {
				...FieldFlags
			}
			defaultVendor {
				...FieldFlags
			}
			deplete {
				...FieldFlags
			}
			description {
				...FieldFlags
			}
			distributorCorePrice {
				...FieldFlags
			}
			distributorPrice {
				...FieldFlags
			}
			freezeDate {
				...FieldFlags
			}
			glCategory {
				...FieldFlags
			}
			interchangeNumber {
				...FieldFlags
			}
			internalNotes {
				...FieldFlags
			}
			inventoryType {
				...FieldFlags
			}
			jobberCorePrice {
				...FieldFlags
			}
			jobberPrice {
				...FieldFlags
			}
			listPrice {
				...FieldFlags
			}
			manufacturer {
				...FieldFlags
			}
			maxQuantity {
				...FieldFlags
			}
			minQuantity {
				...FieldFlags
			}
			model {
				...FieldFlags
			}
			oemNumber {
				...FieldFlags
			}
			parentManufacturer {
				...FieldFlags
			}
			parentModel {
				...FieldFlags
			}
			partNumber {
				...FieldFlags
			}
			popularityCode {
				...FieldFlags
			}
			printTag {
				...FieldFlags
			}
			public {
				...FieldFlags
			}
			purchaseFactor {
				...FieldFlags
			}
			quantity {
				...FieldFlags
			}
			replenish {
				...FieldFlags
			}
			retailPrice {
				...FieldFlags
			}
			retailCorePrice {
				...FieldFlags
			}
			returnable {
				...FieldFlags
			}
			returnableToVendor {
				...FieldFlags
			}
			saleClassCode {
				...FieldFlags
			}
			sellPriceClass {
				...FieldFlags
			}
			serialized {
				...FieldFlags
			}
			safetyStockPercent {
				...FieldFlags
			}
			seasonal {
				...FieldFlags
			}
			sellPackage {
				...FieldFlags
			}
			shippingHeight {
				...FieldFlags
			}
			shippingLength {
				...FieldFlags
			}
			shippingMeasurementUnit {
				...FieldFlags
			}
			shippingWidth {
				...FieldFlags
			}
			side {
				...FieldFlags
			}
			singleQuantity {
				...FieldFlags
			}
			status {
				...FieldFlags
			}
			stockMethod {
				...FieldFlags
			}
			stockType {
				...FieldFlags
			}
			stockingDays {
				...FieldFlags
			}
			tagNumber {
				...FieldFlags
			}
			taxable {
				...FieldFlags
			}
			typeData1 {
				...FieldFlags
			}
			typeData2 {
				...FieldFlags
			}
			typeData3 {
				...FieldFlags
			}
			typeData4 {
				...FieldFlags
			}
			userStatus {
				...FieldFlags
			}
			vehicleMake {
				...FieldFlags
			}
			vehicleModel {
				...FieldFlags
			}
			vendorProductCode {
				...FieldFlags
			}
			vendorPopularityCode {
				...FieldFlags
			}
			vin {
				...FieldFlags
			}
			weight {
				...FieldFlags
			}
			weightUnit {
				...FieldFlags
			}
			wholesalePrice {
				...FieldFlags
			}
			wholesaleCorePrice {
				...FieldFlags
			}
			year {
				...FieldFlags
			}
		}
	}
`)

const settingValuesQuery = graphql(`
	query LIB_settingValues {
		userSettings {
			parts {
				defaultNewInventoryPrintTags
				showSkuField
				showTagNumberWhenCreating
			}
		}
		settingValues {
			inventory {
				...InventorySettings
			}
			location {
				enforceLocationHierarchy
			}
			accounting {
				useGlAccounting
			}
		}
	}
`)

const stageOneQuery = graphql(`
	query PartScreenStageOne {
		inventoryConditions
		userPartStatusList: userStatuses(type: INVENTORY) {
			type
			status
			trigger
			partStatus
			description
		}
		userVehicleStatusList: userStatuses(type: VEHICLE) {
			type
			status
			trigger
			partStatus
			description
		}
		inventoryTypeList: inventoryTypes(filter: { active: true }) {
			inventoryTypeId: id
			typeSetId: setId
			name
			vehicle
			categories {
				id
				name
				description
			}
			partManufacturers: manufacturers {
				id
				name
				code
				active
			}
			requireSerialization
		}
		glCategories {
			id
			name
		}
		vehicleMakes
		vendors(pagination: { pageSize: 0 }) {
			items {
				id
				# active
				code
				companyName
				contactName
				# stockVendor
			}
		}
		sellPriceClasses {
			id
			name
			parent {
				id
				name
			}
		}
		saleClasses {
			code
			name
		}
		session {
			permissionLevels {
				PARTS_CAN_EDIT_INFO
				PARTS_CAN_EDIT_COST
				PARTS_CAN_EDIT_PRICES
				PARTS_CAN_EDIT_QUANTITY
				PARTS_CAN_EDIT_GLCATEGORY
				PARTS_CAN_EDIT_STATUS
			}
			userAccount {
				authorizedStores {
					id
				}
			}
		}
	}
`)

const partStoreDataQuery = graphql(`
	query LIB_partStoreData($storeId: Int!) {
		locations(filter: { allowInventory: true, storeIds: [$storeId] }) {
			id
			name
			description
		}
		store(id: $storeId) {
			settingValues {
				parts {
					clearLocationsOnCopy
				}
			}
		}
	}
`)

const setUserSetting = graphql(`
	mutation Parts_SetUserSetting($input: SetUserSettingInput!) {
		setUserSetting(input: $input)
	}
`)

export type Fields = SettingValues['inventory']['fields']

export const curryIsEditable = (settingValues: Fields) => (fieldName: keyof Fields) => {
	return settingValues[fieldName].editable
}

export const curryIsGlobal = (settingValues: Fields) => (fieldName: keyof Fields) => {
	return settingValues[fieldName].global
}

export const curryGetUserPermissionLevel = (permissionLevels: {
	PARTS_CAN_EDIT_INFO: EnterprisePermissionValue
	PARTS_CAN_EDIT_COST: EnterprisePermissionValue
	PARTS_CAN_EDIT_PRICES: EnterprisePermissionValue
	PARTS_CAN_EDIT_QUANTITY: EnterprisePermissionValue
	PARTS_CAN_EDIT_GLCATEGORY: EnterprisePermissionValue
	PARTS_CAN_EDIT_STATUS: EnterprisePermissionValue
}): ((fieldName: keyof Fields) => EnterprisePermissionValue) => {
	return (fieldName: keyof Fields) => {
		switch (fieldName) {
			case 'coreCost':
			case 'cost': {
				return permissionLevels.PARTS_CAN_EDIT_COST
			}
			case 'quantity': {
				return permissionLevels.PARTS_CAN_EDIT_QUANTITY
			}
			case 'glCategory': {
				return permissionLevels.PARTS_CAN_EDIT_GLCATEGORY
			}
			case 'retailPrice':
			case 'wholesalePrice':
			case 'jobberPrice':
			case 'distributorPrice':
			case 'listPrice':
			case 'retailCorePrice':
			case 'wholesaleCorePrice':
			case 'jobberCorePrice':
			case 'distributorCorePrice': {
				return permissionLevels.PARTS_CAN_EDIT_PRICES
			}
			case 'status': {
				return permissionLevels.PARTS_CAN_EDIT_STATUS
			}
			default:
				return permissionLevels.PARTS_CAN_EDIT_INFO
		}
	}
}

export type GetFieldEditabilityFn = (fieldName: keyof Fields | 'customField') => boolean

export const curryUserCanEdit = (
	fieldSettings: Fields,
	authorizedStores: Set<number>,
	selectedPartStore: number,
	allStoresWherePresent: number[],
	userPermissions: Parameters<typeof curryGetUserPermissionLevel>[0],
) => {
	const isEditable = curryIsEditable(fieldSettings)
	const isGlobal = curryIsGlobal(fieldSettings)
	const getUserPermissionLevel = curryGetUserPermissionLevel(userPermissions)
	const hasCurrentStoreCoverage = authorizedStores.has(selectedPartStore)
	const hasAllStoreCoverage = allStoresWherePresent.every(partStoreId => authorizedStores.has(partStoreId))

	return (fieldName: keyof Fields | 'customField'): boolean => {
		if (fieldName === 'customField') {
			return hasAllStoreCoverage // custom fields are always editable AND global
		} else if (!isEditable(fieldName)) {
			return false
		}

		// Get the user's permission level for the field.
		const level = getUserPermissionLevel(fieldName)

		if (level === 'STORE') {
			return isGlobal(fieldName) ? hasAllStoreCoverage : hasCurrentStoreCoverage
		} else {
			// If the user has global permission, return `true`.
			return level === 'GLOBAL' //Only other level is 'NONE'
		}
	}
}
